"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import AuthButton from "./AuthButton";
import { useRouter } from "next/navigation";
import { signIn } from "@/core/auth/action.auth";
import authSettings from "@/shared/config/authSettings";
import { userCredentials } from "./validation";
import { Box, Field, Stack, Input, Text } from "@chakra-ui/react";
import {
  isMcpConsentAuthorizationTarget,
  normalizeSafeInternalTarget,
} from "./authRedirectUtils";
import { authPageContent } from "./content";

type LoginFormData = z.infer<typeof userCredentials>;

type LogInFormProps = {
  targetUrl?: string;
};

export default function LogInForm({ targetUrl }: LogInFormProps) {
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const safeTargetUrl = normalizeSafeInternalTarget(targetUrl);

  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(userCredentials),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);

    // Create FormData to maintain compatibility with your server action
    const formData = new FormData();
    formData.append("email", data.email);
    formData.append("password", data.password);

    // Call server action
    try {
      const result = await signIn(formData);

      // Handle the result
      if (result.status === "success") {
        if (isMcpConsentAuthorizationTarget(safeTargetUrl)) {
          router.replace(safeTargetUrl);
          return;
        }
        router.push(safeTargetUrl ?? authSettings.redirects.privateHome);
      } else {
        setError(result.status);
      }
    } catch {
      setError(
        "Authentication request failed. Please check your connection and retry.",
      );
    }
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="4">
          <Field.Root invalid={!!errors.email}>
            <Field.Label>Email</Field.Label>
            <Input
              {...register("email")}
              id="email"
              type="email"
              placeholder="Email"
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <Field.ErrorText color="moveon.danger">
                {errors.email.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.password}>
            <Field.Label>Password</Field.Label>
            <Input
              {...register("password")}
              id="password"
              type="password"
              placeholder="Password"
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <Field.ErrorText color="moveon.danger">
                {errors.password.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <AuthButton
            type={authPageContent.forms.login.submitLabel}
            loading={isSubmitting}
          />

          {error && (
            <Text
              mt="1"
              fontSize="sm"
              color="moveon.danger"
              lineHeight="tall"
            >
              {error}
            </Text>
          )}
        </Stack>
      </form>
    </Box>
  );
}
